import React, { useState } from 'react';
import {
    Stack,
    DetailsList,
    DetailsListLayoutMode,
    IColumn,
    SelectionMode,
    DefaultButton,
    Text,
    MessageBar,
    MessageBarType,
    IconButton,
    TooltipHost,
    DirectionalHint,
    Dropdown,
    TextField
} from '@fluentui/react';
import { convertToCSV, downloadCSV } from '../../utils/csvExport.ts';

interface QueryBuilderResponse {
    rows: Record<string, any>[];
    totalRows: number;
    isTruncated: boolean;
    columns: { name: string; dataType: string }[];
}

interface ResultsTableProps {
    results: QueryBuilderResponse;
    tableName: string;
}

interface ColumnFilter {
    columnName: string;
    filterText: string;
}

const ResultsTable: React.FC<ResultsTableProps> = ({ results, tableName }) => {
    const pageSize = 100;
    const [currentPage, setCurrentPage] = useState(0);
    const [copiedCell, setCopiedCell] = useState<string | null>(null);

    // Sort state
    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const [sortAscending, setSortAscending] = useState<boolean>(true);

    // Filter state
    const [activeFilters, setActiveFilters] = useState<ColumnFilter[]>([]);
    const [filterColumnName, setFilterColumnName] = useState<string>('');
    const [filterText, setFilterText] = useState<string>('');

    const handleDownloadCSV = () => {
        // Apply same filtering and sorting as displayed
        let exportRows = filterRows(results.rows, activeFilters);
        if (sortColumn) {
            exportRows = sortRows(exportRows, sortColumn, sortAscending);
        }

        const csv = convertToCSV({ rows: exportRows });
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
        const suffix = activeFilters.length > 0 ? '_filtered' : '';
        const filename = `query_results_${tableName}${suffix}_${timestamp}.csv`;
        downloadCSV(csv, filename);
    };

    const handleCellClick = (value: any, columnName: string, rowIndex: number) => {
        const textToCopy = value === null || value === undefined ? 'null' : String(value);
        navigator.clipboard.writeText(textToCopy);
        const cellKey = `${rowIndex}-${columnName}`;
        setCopiedCell(cellKey);
        setTimeout(() => setCopiedCell(null), 2000); // Clear after 2 seconds
    };

    const sortRows = (rows: Record<string, any>[], colName: string, ascending: boolean) => {
        return [...rows].sort((a, b) => {
            const aVal = a[colName];
            const bVal = b[colName];

            // Handle null/undefined values
            if (aVal === null || aVal === undefined) return 1;
            if (bVal === null || bVal === undefined) return -1;

            // Numeric comparison
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return ascending ? aVal - bVal : bVal - aVal;
            }

            // Date comparison
            if (aVal instanceof Date && bVal instanceof Date) {
                return ascending
                    ? aVal.getTime() - bVal.getTime()
                    : bVal.getTime() - aVal.getTime();
            }

            // String comparison (default)
            const aStr = String(aVal).toLowerCase();
            const bStr = String(bVal).toLowerCase();
            return ascending
                ? aStr.localeCompare(bStr)
                : bStr.localeCompare(aStr);
        });
    };

    const handleColumnHeaderClick = (ev?: React.MouseEvent<HTMLElement>, column?: IColumn) => {
        if (!column) return;

        if (sortColumn === column.fieldName) {
            // Toggle sort direction on same column
            setSortAscending(!sortAscending);
        } else {
            // New column - default to ascending
            setSortColumn(column.fieldName || null);
            setSortAscending(true);
        }
    };

    const filterRows = (rows: Record<string, any>[], filters: ColumnFilter[]) => {
        if (filters.length === 0) return rows;

        return rows.filter(row => {
            return filters.every(filter => {
                const cellValue = row[filter.columnName];
                const cellStr = String(cellValue ?? '').toLowerCase();
                const filterStr = filter.filterText.toLowerCase();
                return cellStr.includes(filterStr);
            });
        });
    };

    const addFilter = () => {
        if (!filterColumnName || !filterText) return;

        // Don't add duplicate filters for same column
        const existingFilter = activeFilters.find(f => f.columnName === filterColumnName);
        if (existingFilter) {
            // Update existing filter
            setActiveFilters(activeFilters.map(f =>
                f.columnName === filterColumnName
                    ? { ...f, filterText }
                    : f
            ));
        } else {
            // Add new filter
            setActiveFilters([...activeFilters, { columnName: filterColumnName, filterText }]);
        }

        // Reset inputs
        setFilterColumnName('');
        setFilterText('');
        setCurrentPage(0); // Reset to first page when filtering
    };

    const removeFilter = (columnName: string) => {
        setActiveFilters(activeFilters.filter(f => f.columnName !== columnName));
        setCurrentPage(0);
    };

    const clearAllFilters = () => {
        setActiveFilters([]);
        setCurrentPage(0);
    };

    if (!results || results.rows.length === 0) {
        return (
            <Stack tokens={{ childrenGap: 10 }}>
                <Text variant="large" styles={{ root: { fontWeight: 600 } }}>
                    Query Results
                </Text>
                <MessageBar messageBarType={MessageBarType.info}>
                    No results found. The query returned 0 rows.
                </MessageBar>
            </Stack>
        );
    }

    // Data processing pipeline: filter → sort → paginate
    // Step 1: Apply filters
    let processedRows = filterRows(results.rows, activeFilters);

    // Step 2: Apply sorting
    if (sortColumn) {
        processedRows = sortRows(processedRows, sortColumn, sortAscending);
    }

    // Step 3: Apply pagination
    const totalPages = Math.ceil(processedRows.length / pageSize);
    const startIndex = currentPage * pageSize;
    const endIndex = Math.min(startIndex + pageSize, processedRows.length);
    const currentPageRows = processedRows.slice(startIndex, endIndex);

    const goToFirstPage = () => setCurrentPage(0);
    const goToLastPage = () => setCurrentPage(totalPages - 1);
    const goToPreviousPage = () => setCurrentPage(Math.max(0, currentPage - 1));
    const goToNextPage = () => setCurrentPage(Math.min(totalPages - 1, currentPage + 1));

    // Generate columns from result metadata
    const columns: IColumn[] = results.columns.map((col, index) => ({
        key: col.name,
        name: col.name,
        fieldName: col.name,
        minWidth: 100,
        maxWidth: 300,
        isResizable: true,
        isSorted: sortColumn === col.name,
        isSortedDescending: sortColumn === col.name ? !sortAscending : undefined,
        onColumnClick: handleColumnHeaderClick,
        onRender: (item: Record<string, any>, itemIndex?: number) => {
            const value = item[col.name];
            const rowIndex = itemIndex ?? 0;
            const cellKey = `${rowIndex}-${col.name}`;
            const isCopied = copiedCell === cellKey;

            let displayValue: string;
            if (value === null || value === undefined) {
                displayValue = 'null';
            } else if (col.dataType.toLowerCase().includes('date') && value instanceof Date) {
                displayValue = value.toLocaleDateString();
            } else if (typeof value === 'number') {
                displayValue = value.toLocaleString();
            } else {
                displayValue = String(value);
            }

            const fullValue = value === null || value === undefined ? 'null' : String(value);

            return (
                <TooltipHost
                    content={isCopied ? 'Copied!' : `Click to copy: ${fullValue}`}
                    directionalHint={DirectionalHint.topCenter}
                >
                    <div
                        onClick={() => handleCellClick(value, col.name, rowIndex)}
                        style={{
                            cursor: 'pointer',
                            padding: '4px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: value === null || value === undefined ? '#a19f9d' : 'inherit',
                            fontStyle: value === null || value === undefined ? 'italic' : 'normal',
                            backgroundColor: isCopied ? '#d4edda' : 'transparent',
                            transition: 'background-color 0.3s'
                        }}
                        title={fullValue}
                    >
                        {displayValue}
                    </div>
                </TooltipHost>
            );
        }
    }));

    return (
        <Stack tokens={{ childrenGap: 15 }} styles={{
            root: {
                marginTop: 20,
                padding: 15,
                border: '1px solid #edebe9',
                borderRadius: 4,
                backgroundColor: '#ffffff'
            }
        }}>
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                <Text variant="large" styles={{ root: { fontWeight: 600 } }}>
                    Query Results ({results.totalRows} rows)
                </Text>
                <DefaultButton
                    text="Download CSV"
                    iconProps={{ iconName: 'Download' }}
                    onClick={handleDownloadCSV}
                />
            </Stack>

            {results.isTruncated && (
                <MessageBar messageBarType={MessageBarType.warning}>
                    Results were truncated at {results.totalRows} rows. Use filters to narrow down your query or increase the row limit.
                </MessageBar>
            )}

            {/* Filter Controls */}
            {results.rows.length > 0 && (
                <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="end" styles={{
                    root: {
                        padding: '10px',
                        backgroundColor: '#f3f2f1',
                        borderRadius: 4,
                        marginTop: 10
                    }
                }}>
                    <Dropdown
                        label="Filter Column"
                        placeholder="Select column"
                        options={results.columns.map(col => ({ key: col.name, text: col.name }))}
                        selectedKey={filterColumnName}
                        onChange={(_, option) => setFilterColumnName(option?.key as string || '')}
                        styles={{ root: { width: 200 } }}
                    />
                    <TextField
                        label="Filter Value"
                        placeholder="Enter text to filter"
                        value={filterText}
                        onChange={(_, newValue) => setFilterText(newValue || '')}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                addFilter();
                            }
                        }}
                        styles={{ root: { width: 250 } }}
                    />
                    <DefaultButton
                        text="Add Filter"
                        iconProps={{ iconName: 'Filter' }}
                        onClick={addFilter}
                        disabled={!filterColumnName || !filterText}
                    />
                    {activeFilters.length > 0 && (
                        <DefaultButton
                            text="Clear All"
                            iconProps={{ iconName: 'ClearFilter' }}
                            onClick={clearAllFilters}
                        />
                    )}
                </Stack>
            )}

            {/* Active Filters Display */}
            {activeFilters.length > 0 && (
                <Stack horizontal wrap tokens={{ childrenGap: 5 }} styles={{
                    root: { padding: '5px 10px' }
                }}>
                    <Text variant="small" styles={{ root: { fontWeight: 600, marginRight: 5 } }}>
                        Active Filters:
                    </Text>
                    {activeFilters.map(filter => (
                        <Stack key={filter.columnName} horizontal verticalAlign="center" tokens={{ childrenGap: 5 }} styles={{
                            root: {
                                padding: '2px 8px',
                                backgroundColor: '#0078d4',
                                color: 'white',
                                borderRadius: 2,
                                fontSize: 11
                            }
                        }}>
                            <Text variant="small" styles={{ root: { color: 'white' } }}>
                                {filter.columnName}: "{filter.filterText}"
                            </Text>
                            <IconButton
                                iconProps={{ iconName: 'Cancel' }}
                                title="Remove filter"
                                ariaLabel="Remove filter"
                                onClick={() => removeFilter(filter.columnName)}
                                styles={{
                                    root: {
                                        color: 'white',
                                        width: 16,
                                        height: 16
                                    },
                                    icon: { fontSize: 10 }
                                }}
                            />
                        </Stack>
                    ))}
                </Stack>
            )}

            {/* Pagination Controls - Top */}
            {totalPages > 1 && (
                <Stack horizontal horizontalAlign="space-between" verticalAlign="center" styles={{
                    root: { padding: '10px 0', borderBottom: '1px solid #edebe9' }
                }}>
                    <Text variant="small">
                        Showing {startIndex + 1}-{endIndex} of {processedRows.length} rows
                        {activeFilters.length > 0 && ` (filtered from ${results.totalRows})`}
                    </Text>
                    <Stack horizontal tokens={{ childrenGap: 5 }} verticalAlign="center">
                        <IconButton
                            iconProps={{ iconName: 'DoubleChevronLeft' }}
                            title="First page"
                            ariaLabel="First page"
                            onClick={goToFirstPage}
                            disabled={currentPage === 0}
                        />
                        <IconButton
                            iconProps={{ iconName: 'ChevronLeft' }}
                            title="Previous page"
                            ariaLabel="Previous page"
                            onClick={goToPreviousPage}
                            disabled={currentPage === 0}
                        />
                        <Text variant="small" styles={{ root: { padding: '0 10px' } }}>
                            Page {currentPage + 1} of {totalPages}
                        </Text>
                        <IconButton
                            iconProps={{ iconName: 'ChevronRight' }}
                            title="Next page"
                            ariaLabel="Next page"
                            onClick={goToNextPage}
                            disabled={currentPage === totalPages - 1}
                        />
                        <IconButton
                            iconProps={{ iconName: 'DoubleChevronRight' }}
                            title="Last page"
                            ariaLabel="Last page"
                            onClick={goToLastPage}
                            disabled={currentPage === totalPages - 1}
                        />
                    </Stack>
                </Stack>
            )}

            <DetailsList
                items={currentPageRows}
                columns={columns}
                layoutMode={DetailsListLayoutMode.justified}
                selectionMode={SelectionMode.none}
                compact={true}
                styles={{
                    root: {
                        '.ms-DetailsRow': {
                            ':hover': {
                                backgroundColor: '#f3f2f1'
                            }
                        }
                    }
                }}
            />

            {/* Pagination Controls - Bottom */}
            {totalPages > 1 && (
                <Stack horizontal horizontalAlign="center" verticalAlign="center" styles={{
                    root: { padding: '10px 0', borderTop: '1px solid #edebe9' }
                }}>
                    <Stack horizontal tokens={{ childrenGap: 5 }} verticalAlign="center">
                        <IconButton
                            iconProps={{ iconName: 'DoubleChevronLeft' }}
                            title="First page"
                            ariaLabel="First page"
                            onClick={goToFirstPage}
                            disabled={currentPage === 0}
                        />
                        <IconButton
                            iconProps={{ iconName: 'ChevronLeft' }}
                            title="Previous page"
                            ariaLabel="Previous page"
                            onClick={goToPreviousPage}
                            disabled={currentPage === 0}
                        />
                        <Text variant="small" styles={{ root: { padding: '0 10px' } }}>
                            Page {currentPage + 1} of {totalPages}
                        </Text>
                        <IconButton
                            iconProps={{ iconName: 'ChevronRight' }}
                            title="Next page"
                            ariaLabel="Next page"
                            onClick={goToNextPage}
                            disabled={currentPage === totalPages - 1}
                        />
                        <IconButton
                            iconProps={{ iconName: 'DoubleChevronRight' }}
                            title="Last page"
                            ariaLabel="Last page"
                            onClick={goToLastPage}
                            disabled={currentPage === totalPages - 1}
                        />
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
};

export default ResultsTable;
